import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import './Events.css'
import backgroundImage from './Events/assets/Gemini_Generated_Image_gniijagniijagnii.png'

// Helper to transform master events to Events component format
const transformMasterEvent = (masterEvent, index) => {
  // Extract date from start_time or use date field
  let dateStr = masterEvent.date || '2025-11-01'
  if (!dateStr || dateStr === '2025-11-01') {
    if (masterEvent.start_time) {
      try {
        // Try to parse various date formats
        const dateMatch = masterEvent.start_time.match(/(\d{4 свидания-\d{2}-\d{2})|(\w+ \d+, \d{4})|(\d{1,2}\/\d{1,2}\/\d{4})/)
        if (dateMatch) {
          const date = new Date(masterEvent.start_time)
          if (!isNaN(date.getTime())) {
            dateStr = date.toISOString().split('T')[0]
          }
        } else {
          // Try parsing the whole string
          const date = new Date(masterEvent.start_time)
          if (!isNaN(date.getTime())) {
            dateStr = date.toISOString().split('T')[0]
          }
        }
      } catch (e) {
        // Use default
      }
    }
  }
  
  // Get address first - use location/address or address field
  let address = masterEvent['location/address'] || masterEvent.address || ''
  
  // Get venue name - try multiple fields
  let venue = masterEvent.venueName || masterEvent.venue
  
  // If no venue or venue is same as address, extract venue from address
  if (!venue || venue === address) {
    if (address && address.includes(',')) {
      // Extract venue from address (first part before first comma)
      const addressParts = address.split(',')
      venue = addressParts[0].trim()
      // Address stays as full address
    } else if (address) {
      // If no comma, use first 2 words as venue
      const words = address.split(' ').slice(0, 2)
      venue = words.join(' ')
    } else {
      // Fallback to event title
      venue = masterEvent.event_title || masterEvent.eventTitle || masterEvent.name || 'Venue TBD'
    }
  }
  
  // If still no address, use venue
  if (!address || address.length < 3) {
    address = venue || 'Address TBD'
    if (address.length < 3) {
      address = address + ', Location TBD'
    }
  }
  
  // Ensure venue has minimum length
  if (venue.length < 3) {
    venue = venue + ' Venue'
  }
  
  // Trim to max lengths
  venue = venue.substring(0, 100)
  address = address.substring(0, 200)
  
  // Get event name
  const name = masterEvent.event_title || masterEvent.name || masterEvent.eventTitle || `Event ${index + 1}`
  
  return {
    id: masterEvent.id || `master-${index}`,
    date: dateStr,
    name: name,
    venue: venue,
    address: address
  }
}

// Load master events from window event
const loadMasterEvents = () => {
  return new Promise((resolve) => {
    // Check if already loaded
    if (window.masterEventsData && window.masterEventsData.length > 0) {
      const transformed = window.masterEventsData.map(transformMasterEvent)
      resolve(transformed)
      return
    }
    
    // Listen for master events loaded event
    const handleMasterEventsLoaded = () => {
      if (window.masterEventsData && window.masterEventsData.length > 0) {
        const transformed = window.masterEventsData.map(transformMasterEvent)
        window.removeEventListener('masterEventsLoaded', handleMasterEventsLoaded)
        resolve(transformed)
      }
    }
    
    window.addEventListener('masterEventsLoaded', handleMasterEventsLoaded)
    
    // Also try to access via global
    if (window.MASTER_EVENTS) {
      const transformed = window.MASTER_EVENTS.map(transformMasterEvent)
      window.removeEventListener('masterEventsLoaded', handleMasterEventsLoaded)
      resolve(transformed)
      return
    }
    
    // Timeout after 5 seconds
    setTimeout(() => {
      window.removeEventListener('masterEventsLoaded', handleMasterEventsLoaded)
      resolve([])
    }, 5000)
  })
}

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================
const ADMIN_PASSWORD_HASH = '240be518fabd2724ddb6f04eeb1da5967448d7e831c08c8fa822809f74c720a9' // SHA-256 of "admin123"
const MAX_UNDO_STACK = 10
const AUTO_SAVE_DELAY = 1000
const SESSION_TIMEOUT = 30 * 60 * 1000 // 30 minutes
const RATE_LIMIT_ATTEMPTS = 3
const RATE_LIMIT_LOCKOUT = 5 * 60 * 1000 // 5 minutes

// Sample dataset - 20 events across multiple dates starting Friday, October 31
const SAMPLE_EVENTS = [
  // Friday, October 31, 2025 - Halloween
  { id: "evt001", date: "2025-10-31", name: "Halloween Night Market", venue: "Canal Market Hall", address: "123 Canal St, New York, NY" },
  { id: "evt002", date: "2025-10-31", name: "Downtown Jazz Fest", venue: "Broadway Theater", address: "200 Broadway, New York, NY" },
  { id: "evt003", date: "2025-10-31", name: "Spooky Movie Marathon", venue: "Film Forum Cinema", address: "209 W Houston St, New York, NY" },
  { id: "evt004", date: "2025-10-31", name: "Halloween Costume Party", venue: "Brooklyn Bowl", address: "61 Wythe Ave, Brooklyn, NY" },
  
  // Saturday, November 1, 2025
  { id: "evt005", date: "2025-11-01", name: "Park Picnic & Concert", venue: "Central Park", address: "Central Park West, New York, NY" },
  { id: "evt006", date: "2025-11-01", name: "Art Walk & Gallery Tour", venue: "Chelsea Gallery District", address: "45 E 12th St, New York, NY" },
  { id: "evt007", date: "2025-11-01", name: "Live Music Night", venue: "Blue Note Jazz Club", address: "131 W 3rd St, New York, NY" },
  { id: "evt008", date: "2025-11-01", name: "Farmers Market", venue: "Union Square Greenmarket", address: "Union Square West, New York, NY" },
  
  // Sunday, November 2, 2025
  { id: "evt009", date: "2025-11-02", name: "Tech Meetup & Networking", venue: "WeWork Innovation Hub", address: "600 Broadway, New York, NY" },
  { id: "evt010", date: "2025-11-02", name: "Food Festival", venue: "Smorgasburg Market", address: "90 Kent Ave, Brooklyn, NY" },
  { id: "evt011", date: "2025-11-02", name: "Brunch & Music", venue: "The Smith Midtown", address: "956 2nd Ave, New York, NY" },
  { id: "evt012", date: "2025-11-02", name: "Vintage Flea Market", venue: "Hell's Kitchen Flea", address: "39th St & 9th Ave, New York, NY" },
  
  // Monday, November 3, 2025
  { id: "evt013", date: "2025-11-03", name: "Comedy Show Open Mic", venue: "Comedy Cellar", address: "117 MacDougal St, New York, NY" },
  { id: "evt014", date: "2025-11-03", name: "Yoga in the Park", venue: "Bryant Park", address: "42nd St & 6th Ave, New York, NY" },
  { id: "evt015", date: "2025-11-03", name: "Book Club Discussion", venue: "The Strand Bookstore", address: "828 Broadway, New York, NY" },
  
  // Tuesday, November 4, 2025
  { id: "evt016", date: "2025-11-04", name: "Trivia Night", venue: "Brooklyn Barge", address: "3 Columbia Pl, Brooklyn, NY" },
  { id: "evt017", date: "2025-11-04", name: "Wine Tasting", venue: "Eataly Flatiron", address: "200 5th Ave, New York, NY" },
  { id: "evt018", date: "2025-11-04", name: "Dance Class", venue: "Alvin Ailey Extension", address: "405 W 55th St, New York, NY" },
  
  // Wednesday, November 5, 2025
  { id: "evt019", date: "2025-11-05", name: "Concert Series", venue: "Radio City Music Hall", address: "1260 6th Ave, New York, NY" },
  { id: "evt020", date: "2025-11-05", name: "Cooking Workshop", venue: "Institute of Culinary Education", address: "225 Liberty St, New York, NY" }
]

const Events = () => {
  const location = useLocation()
  
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [events, setEvents] = useState([])
  const [filteredEvents, setFilteredEvents] = useState([])
  const [isAdminMode, setIsAdminMode] = useState(false)
  const [selectedEventId, setSelectedEventId] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)
  const [undoStack, setUndoStack] = useState([])
  const [redoStack, setRedoStack] = useState([])
  const [toasts, setToasts] = useState([])
  const [showSettings, setShowSettings] = useState(false)
  const [activityLog, setActivityLog] = useState([])
  const [lastActivity, setLastActivity] = useState(Date.now())
  const [failedLoginAttempts, setFailedLoginAttempts] = useState(0)
  const [lockoutUntil, setLockoutUntil] = useState(null)

  // Quick Add state
  const [showQuickAddModal, setShowQuickAddModal] = useState(false)
  const [showQuickAddPreview, setShowQuickAddPreview] = useState(false)
  const [quickAddInput, setQuickAddInput] = useState('')
  const [quickAddFormat, setQuickAddFormat] = useState('auto')
  const [quickAddOptions, setQuickAddOptions] = useState({
    skipDuplicates: false,
    showPreview: true
  })
  const [quickAddParsed, setQuickAddParsed] = useState({
    valid: [],
    errors: [],
    duplicates: []
  })

  // My Events state
  const [savedEvents, setSavedEvents] = useState([])

  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    name: '',
    venue: '',
    address: ''
  })
  const [formErrors, setFormErrors] = useState({})

  // Refs
  const searchInputRef = useRef(null)
  const tableWrapperRef = useRef(null)
  const scrollShadowTopRef = useRef(null)
  const scrollShadowBottomRef = useRef(null)
  const debounceTimerRef = useRef(null)
  const autoSaveTimerRef = useRef(null)
  const keyboardNavIndexRef = useRef(-1)
  const quickAddTextareaRef = useRef(null)
  const touchStartRef = useRef({})

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  
  /**
   * Formats ISO date string to human-readable format
   */
  const formatDate = (isoString) => {
    try {
      const date = new Date(isoString + 'T00:00:00')
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', isoString)
        return 'Invalid Date'
      }
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch (error) {
      console.error('Error formatting date:', error)
      return 'Invalid Date'
    }
  }

  /**
   * Groups events by formatted date
   */
  const groupByDate = (eventsList) => {
    const grouped = {}
    eventsList.forEach(event => {
      if (!event.date) return
      const dateKey = formatDate(event.date)
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(event)
    })
    // Sort events within each group by name
    Object.keys(grouped).forEach(dateKey => {
      grouped[dateKey].sort((a, b) => a.name.localeCompare(b.name))
    })
    return grouped
  }

  /**
   * Generates unique ID
   */
  const generateId = () => {
    return `evt${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Validates event object
   */
  const validateEvent = (event) => {
    const errors = []
    
    if (!event.id) errors.push('ID is required')
    if (!event.date) {
      errors.push('Date is required')
    } else {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/
      if (!dateRegex.test(event.date)) {
        errors.push('Date must be in YYYY-MM-DD format')
      }
    }
    
    if (!event.name || event.name.trim().length < 3) {
      errors.push('Event name must be at least 3 characters')
    } else if (event.name.length > 100) {
      errors.push('Event name must be 100 characters or less')
    } else {
      const nameRegex = /^[a-zA-Z0-9\s\-,'.&]+$/
      if (!nameRegex.test(event.name)) {
        errors.push('Event name contains invalid characters')
      }
    }
    
    if (!event.venue || event.venue.trim().length < 3) {
      errors.push('Venue must be at least 3 characters')
    } else if (event.venue.length > 100) {
      errors.push('Venue must be 100 characters or less')
    }
    
    if (!event.address || event.address.trim().length < 3) {
      errors.push('Address must be at least 3 characters')
    } else if (event.address.length > 200) {
      errors.push('Address must be 200 characters or less')
    }
    
    return {
      valid: errors.length === 0,
      errors
    }
  }

  /**
   * Hashes password using SHA-256
   */
  const hashPassword = async (password) => {
    const encoder = new TextEncoder()
    const data = encoder.encode(password)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  /**
   * Checks for duplicate events
   */
  const checkDuplicate = (event, excludeId = null) => {
    return events.some(e => 
      e.id !== excludeId &&
      e.date === event.date &&
      e.name.toLowerCase() === event.name.toLowerCase() &&
      e.venue.toLowerCase() === event.venue.toLowerCase()
    )
  }

  // ============================================
  // QUICK ADD - PARSING FUNCTIONS
  // ============================================

  /**
   * Detects input format (CSV, TAB, PIPE)
   */
  const detectFormat = (text) => {
    if (!text || text.trim().length === 0) return 'csv'
    
    const firstLine = text.trim().split('\n')[0]
    
    if (firstLine.includes('\t')) {
      return 'tab'
    } else if (firstLine.includes('|')) {
      return 'pipe'
    } else if (firstLine.includes(',')) {
      return 'csv'
    }
    
    return 'csv'
  }

  /**
   * Checks if first line is a header row
   */
  const hasHeaderRow = (line) => {
    const lower = line.toLowerCase()
    return (lower.includes('date') && 
            (lower.includes('event') || lower.includes('name')) &&
            lower.includes('venue'))
  }

  /**
   * Parses date string flexibly
   */
  const parseQuickAddDate = (dateString) => {
    if (!dateString || !dateString.trim()) {
      throw new Error('Date is required')
    }
    
    const cleaned = dateString.trim()
    
    // ISO format (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
      return cleaned
    }
    
    // US format (MM/DD/YYYY or M/D/YYYY)
    const usFormat = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/
    const usMatch = cleaned.match(usFormat)
    if (usMatch) {
      const [_, month, day, year] = usMatch
      const paddedMonth = month.padStart(2, '0')
      const paddedDay = day.padStart(2, '0')
      return `${year}-${paddedMonth}-${paddedDay}`
    }
    
    // Natural language (Oct 31, 2025 or October 31, 2025)
    try {
      const parsed = new Date(cleaned)
      if (!isNaN(parsed.getTime())) {
        return parsed.toISOString().split('T')[0]
      }
    } catch (e) {
      // Continue to error
    }
    
    throw new Error(`Invalid date format: "${dateString}". Use MM/DD/YYYY, Oct 31, 2025, or 2025-10-31`)
  }

  /**
   * Parses CSV line with quoted field support
   */
  const parseCSVLine = (line) => {
    const parts = []
    let current = ''
    let inQuotes = false
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      const nextChar = line[i + 1]
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"'
          i++
        } else {
          inQuotes = !inQuotes
        }
      } else if (char === ',' && !inQuotes) {
        parts.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    
    parts.push(current.trim())
    return parts.filter(p => p.length > 0)
  }

  /**
   * Parses a single line into event object
   */
  const parseQuickAddLine = (line, format, lineNumber) => {
    if (!line || !line.trim()) {
      return null
    }
    
    let parts = []
    
    switch(format) {
      case 'tab':
        parts = line.split('\t').map(p => p.trim()).filter(p => p)
        break
      case 'pipe':
        parts = line.split('|').map(p => p.trim()).filter(p => p)
        break
      case 'csv':
      default:
        parts = parseCSVLine(line)
        break
    }
    
    if (parts.length < 4) {
      throw new Error(`Line ${lineNumber}: Expected 4 fields (Date, Name, Venue, Address), found ${parts.length}`)
    }
    
    if (parts.length > 4) {
      const addressParts = parts.slice(3)
      parts = [parts[0], parts[1], parts[2], addressParts.join(', ')]
    }
    
    const date = parseQuickAddDate(parts[0])
    const name = parts[1] || ''
    const venue = parts[2] || ''
    const address = parts.slice(3).join(', ') || ''
    
    return {
      date,
      name: name.trim(),
      venue: venue.trim(),
      address: address.trim()
    }
  }

  /**
   * Parses bulk input text into events array
   */
  const parseQuickAddInput = (text, format = 'auto', skipHeader = true) => {
    if (!text || !text.trim()) {
      return { valid: [], errors: [], duplicates: [] }
    }
    
    const detectedFormat = format === 'auto' ? detectFormat(text) : format
    const lines = text.trim().split('\n')
    let startIndex = 0
    
    if (skipHeader && lines.length > 0 && hasHeaderRow(lines[0])) {
      startIndex = 1
    }
    
    const valid = []
    const errors = []
    
    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) continue
      
      try {
        const parsed = parseQuickAddLine(line, detectedFormat, i + 1)
        if (!parsed) continue
        
        const eventWithId = { ...parsed, id: generateId() }
        const validation = validateEvent(eventWithId)
        
        if (validation.valid) {
          valid.push(eventWithId)
        } else {
          errors.push({
            line: i + 1,
            text: line,
            event: parsed,
            errors: validation.errors
          })
        }
      } catch (error) {
        errors.push({
          line: i + 1,
          text: line,
          event: null,
          errors: [error.message]
        })
      }
    }
    
    const duplicates = valid.filter(event => checkDuplicate(event))
    
    return {
      valid: valid.filter(e => !checkDuplicate(e)),
      errors,
      duplicates
    }
  }

  // ============================================
  // QUICK ADD - HANDLER FUNCTIONS
  // ============================================

  /**
   * Handles Quick Add input change
   */
  const handleQuickAddInputChange = (e) => {
    setQuickAddInput(e.target.value)
  }

  /**
   * Processes Quick Add input and shows preview
   */
  const handleQuickAddPreview = () => {
    if (!quickAddInput.trim()) {
      showToast('error', 'Please enter events to add')
      return
    }
    
    const parsed = parseQuickAddInput(quickAddInput, quickAddFormat, true)
    setQuickAddParsed(parsed)
    setShowQuickAddPreview(true)
  }

  /**
   * Adds all valid events from Quick Add
   */
  const handleQuickAddSubmit = () => {
    const { valid, duplicates } = quickAddParsed
    let eventsToAdd = [...valid]
    
    if (!quickAddOptions.skipDuplicates && duplicates.length > 0) {
      const duplicateNames = duplicates.slice(0, 3).map(d => d.name).join(', ')
      const moreText = duplicates.length > 3 ? ` and ${duplicates.length - 3} more` : ''
      if (!confirm(`${duplicates.length} duplicate event(s) detected: ${duplicateNames}${moreText}\n\nAdd them anyway?`)) {
        eventsToAdd = valid.filter(e => !duplicates.includes(e))
      } else {
        eventsToAdd = valid
      }
    }
    
    if (eventsToAdd.length === 0) {
      showToast('error', 'No events to add')
      return
    }
    
    const sortedEvents = [...events, ...eventsToAdd].sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date)
      return dateCompare !== 0 ? dateCompare : a.name.localeCompare(b.name)
    })
    
    setEvents(sortedEvents)
    
    addToUndoStack({
      type: 'bulk-add',
      data: eventsToAdd,
      timestamp: Date.now()
    })
    
    addActivityLog('bulk-add', null, `Bulk added ${eventsToAdd.length} events`)
    showToast('success', `Added ${eventsToAdd.length} events successfully`)
    
    setShowQuickAddModal(false)
    setShowQuickAddPreview(false)
    setQuickAddInput('')
    setQuickAddParsed({ valid: [], errors: [], duplicates: [] })
    
    if (eventsToAdd.length > 0) {
      setTimeout(() => {
        const firstEventId = eventsToAdd[0].id
        const eventElement = document.getElementById(`event-${firstEventId}`)
        if (eventElement) {
          eventElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          eventElement.classList.add('highlight-new')
          setTimeout(() => eventElement.classList.remove('highlight-new'), 2000)
        }
      }, 100)
    }
  }

  /**
   * Downloads Quick Add template as CSV
   */
  const handleDownloadTemplate = () => {
    const template = `Date,Event Name,Venue,Address
10/31/2025,Halloween Night Market,Canal Market Hall,123 Canal St, New York, NY
11/01/2025,Park Picnic,Central Park,Central Park West, New York, NY
11/02/2025,Tech Meetup,WeWork Hub,600 Broadway, New York, NY

Instructions:
- Use format: Date, Event Name, Venue, Address
- Date formats supported: 10/31/2025, Oct 31 2025, 2025-10-31
- For addresses with commas, use quotes: "123 Main St, Apt 4, NY"
- Copy rows from Excel/Sheets and paste into Quick Add`
    
    const blob = new Blob([template], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `events_template_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    showToast('success', 'Template downloaded')
  }

  // ============================================
  // TOAST NOTIFICATIONS (must be before My Events functions)
  // ============================================
  
  const showToast = (type, message, options = {}) => {
    const id = `toast-${Date.now()}`
    const toast = { id, type, message, ...options }
    
    setToasts(prev => [...prev, toast])
    
    // Auto-dismiss after 4 seconds (6 for errors)
    const duration = type === 'error' ? 6000 : 4000
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, duration)
  }

  // ============================================
  // MY EVENTS - LOCAL STORAGE FUNCTIONS
  // ============================================

  /**
   * Loads saved events from localStorage
   */
  const loadSavedEvents = useCallback(() => {
    try {
      const stored = localStorage.getItem('myEvents')
      if (stored) {
        const data = JSON.parse(stored)
        if (data.savedEvents && Array.isArray(data.savedEvents)) {
          return data.savedEvents
        }
      }
    } catch (error) {
      console.error('Failed to load saved events:', error)
    }
    return []
  }, [])

  /**
   * Saves events array to localStorage
   */
  const saveSavedEventsToStorage = useCallback((eventsToSave) => {
    try {
      const data = {
        savedEvents: eventsToSave,
        lastModified: new Date().toISOString(),
        version: '1.0'
      }
      localStorage.setItem('myEvents', JSON.stringify(data))
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        showToast('error', 'Storage quota exceeded. Please remove some saved events.')
      } else {
        console.error('Failed to save to localStorage:', error)
      }
    }
  }, [showToast])

  /**
   * Checks if event is already saved
   */
  const isEventSaved = useCallback((eventId) => {
    return savedEvents.some(e => e.id === eventId)
  }, [savedEvents])

  /**
   * Saves an event to My Events
   */
  const saveEventToMyEvents = useCallback((event) => {
    if (!event || !event.id) return
    
    // Check if already saved
    if (isEventSaved(event.id)) {
      showToast('info', 'Event already saved')
      return
    }
    
    // Create saved event object with timestamp
    const savedEvent = {
      ...event,
      savedAt: new Date().toISOString()
    }
    
    // Add to saved events
    const updatedSaved = [...savedEvents, savedEvent].sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date)
      return dateCompare !== 0 ? dateCompare : a.name.localeCompare(b.name)
    })
    
    setSavedEvents(updatedSaved)
    saveSavedEventsToStorage(updatedSaved)
    showToast('success', '✓ Event saved')
  }, [savedEvents, isEventSaved, saveSavedEventsToStorage, showToast])

  // Load saved events on mount
  useEffect(() => {
    const loaded = loadSavedEvents()
    setSavedEvents(loaded)
  }, [loadSavedEvents])

  // ============================================
  // DATA PERSISTENCE
  // ============================================
  
  const saveToLocalStorage = useCallback(() => {
    try {
      const data = {
        events,
        lastModified: new Date().toISOString(),
        version: '1.0'
      }
      localStorage.setItem('eventsData', JSON.stringify(data))
      if (isAdminMode && localStorage.getItem('rememberMe') === 'true') {
        localStorage.setItem('adminToken', 'authenticated')
      }
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        showToast('error', 'Storage quota exceeded. Please export your data.')
      } else {
        console.error('Failed to save to localStorage:', error)
      }
    }
  }, [events, isAdminMode])

  const loadFromLocalStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem('eventsData')
      if (stored) {
        const data = JSON.parse(stored)
        if (data.events && Array.isArray(data.events)) {
          // Validate and filter out invalid events
          const validEvents = data.events.filter(event => {
            const validation = validateEvent(event)
            if (!validation.valid) {
              console.warn('Skipping invalid event:', event, validation.errors)
            }
            return validation.valid
          })
          return validEvents
        }
      }
    } catch (error) {
      console.error('Failed to load from localStorage:', error)
    }
    return null
  }, [])

  const loadActivityLog = () => {
    try {
      const stored = localStorage.getItem('activityLog')
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.error('Failed to load activity log:', error)
    }
    return []
  }

  const saveActivityLog = (log) => {
    try {
      // Keep only last 50 entries
      const limited = log.slice(-50)
      localStorage.setItem('activityLog', JSON.stringify(limited))
    } catch (error) {
      console.error('Failed to save activity log:', error)
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================
  
  useEffect(() => {
    let mounted = true
    let loadTimeout
    
    // Load events from localStorage first
    const loadedEvents = loadFromLocalStorage()
    
    // Check if localStorage has old/stale sample data that should be updated
    // If events count is less than new SAMPLE_EVENTS (20), update to new samples
    const shouldUpdateSamples = loadedEvents && loadedEvents.length > 0 && loadedEvents.length < SAMPLE_EVENTS.length
    
    // If we have stale sample data, clear it and force reload with new samples
    if (shouldUpdateSamples) {
      console.log(`🔄 Detected ${loadedEvents.length} old sample events, updating to ${SAMPLE_EVENTS.length} new sample events`)
      // Clear stale localStorage data
      localStorage.removeItem('eventsData')
    } else if (loadedEvents && loadedEvents.length > 0 && !shouldUpdateSamples) {
      // Only use localStorage if it has valid data (not stale)
      if (mounted) {
        setEvents(loadedEvents)
        setIsLoading(false)
        console.log(`✅ Loaded ${loadedEvents.length} events from localStorage`)
      }
      return // Early return
    }
    
    // Single data loading flow - prevent race conditions
    const loadData = async () => {
      try {
        console.log('🔄 Loading events data...')
        
        // Wait a bit for window.masterEventsData to be available
        // (in case scripts are still loading)
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // If we detected stale samples, force load sample events (skip master events)
        if (shouldUpdateSamples) {
          if (mounted) {
            setEvents(SAMPLE_EVENTS)
            saveToLocalStorage()
            setIsLoading(false)
            console.log(`✅ Loaded ${SAMPLE_EVENTS.length} sample events (updated from stale data)`)
          }
          return
        }
        
        // Try to load master events (only if not updating samples)
        const masterEvents = await loadMasterEvents()
        
        if (!mounted) return
        
        if (masterEvents && masterEvents.length > 0) {
          // Transform and use master events
          const validEvents = masterEvents.filter(event => {
            const validation = validateEvent(event)
            if (!validation.valid) {
              // Reduce console noise - only log first few invalid events
              const invalidCount = masterEvents.filter(e => !validateEvent(e).valid).length
              if (invalidCount <= 5) {
                console.warn('Skipping invalid master event:', event, validation.errors)
              }
            }
            return validation.valid
          })
          
          if (validEvents.length > 0) {
            if (mounted) {
              setEvents(validEvents)
              saveToLocalStorage()
              setIsLoading(false)
              console.log(`✅ Loaded ${validEvents.length} master events`)
            }
            return
          }
        }
        
        // Fallback to sample data if no master events
        if (mounted) {
          setEvents(SAMPLE_EVENTS)
          saveToLocalStorage()
          setIsLoading(false)
          console.log(`✅ Loaded ${SAMPLE_EVENTS.length} sample events`)
        }
      } catch (error) {
        console.error('❌ Error loading master events:', error)
        if (mounted) {
          // Fallback to sample data on error
          setEvents(SAMPLE_EVENTS)
          saveToLocalStorage()
          setIsLoading(false)
          console.log(`✅ Fallback: Loaded ${SAMPLE_EVENTS.length} sample events`)
        }
      }
    }
    
    // Load data
    loadData()
    
    // Cleanup
    return () => {
      mounted = false
      if (loadTimeout) clearTimeout(loadTimeout)
    }
    
    // Load activity log
    setActivityLog(loadActivityLog())
    
    // Check for admin token
    const token = localStorage.getItem('adminToken')
    const rememberMe = localStorage.getItem('rememberMe')
    if (token && rememberMe === 'true') {
      setIsAdminMode(true)
      setLastActivity(Date.now())
    }
    
    // Load failed login attempts
    const storedAttempts = localStorage.getItem('failedLoginAttempts')
    const storedLockout = localStorage.getItem('lockoutUntil')
    if (storedAttempts) {
      setFailedLoginAttempts(parseInt(storedAttempts))
    }
    if (storedLockout) {
      const lockoutTime = parseInt(storedLockout)
      if (lockoutTime > Date.now()) {
        setLockoutUntil(lockoutTime)
      } else {
        localStorage.removeItem('lockoutUntil')
        localStorage.removeItem('failedLoginAttempts')
      }
    }
    
    setIsLoading(false)
    
    // Set up session timeout check
    const sessionInterval = setInterval(() => {
      if (isAdminMode && Date.now() - lastActivity > SESSION_TIMEOUT) {
        exitAdminMode()
        showToast('info', 'Session expired. Please log in again.')
      }
    }, 60000) // Check every minute
    
    return () => clearInterval(sessionInterval)
  }, [])

  // Save to localStorage whenever events change
  useEffect(() => {
    if (!isLoading) {
      saveToLocalStorage()
    }
  }, [events, isLoading, saveToLocalStorage])

  // ============================================
  // SEARCH FUNCTIONALITY
  // ============================================
  
  const handleSearch = useCallback((query) => {
    setSearchQuery(query)
    
    // Debounce search
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    
    debounceTimerRef.current = setTimeout(() => {
      if (!query.trim()) {
        setFilteredEvents(events)
        return
      }
      
      const lowerQuery = query.toLowerCase()
      const filtered = events.filter(event => 
        event.name.toLowerCase().includes(lowerQuery) ||
        event.venue.toLowerCase().includes(lowerQuery) ||
        event.address.toLowerCase().includes(lowerQuery)
      )
      
      setFilteredEvents(filtered)
      
      // Announce results for screen readers
      const announcement = `Showing ${filtered.length} of ${events.length} events`
      const announcementEl = document.getElementById('search-announcement')
      if (announcementEl) {
        announcementEl.textContent = announcement
      }
    }, 300)
  }, [events])

  // ============================================
  // ROW SELECTION & POPUP
  // ============================================
  
  const handleRowClick = (eventId, e) => {
    // Prevent clicking on date headers
    if (e && (e.target.closest('.date-header') || e.currentTarget.classList.contains('date-header'))) {
      return
    }
    setSelectedEventId(eventId === selectedEventId ? null : eventId)
    setLastActivity(Date.now())
    
    // Announce selection for screen readers
    const event = events.find(e => e.id === eventId)
    if (event) {
      const announcement = `Selected: ${event.name} on ${formatDate(event.date)}`
      const announcementEl = document.getElementById('selection-announcement')
      if (announcementEl) {
        announcementEl.textContent = announcement
      }
    }
  }

  const handlePopupAction = (action, eventId) => {
    const event = events.find(e => e.id === eventId)
    if (!event) return
    
    if (action === 'save') {
      saveEventToMyEvents(event)
    } else if (action === 'tickets') {
      console.log('Get tickets for:', event)
      showToast('info', 'Redirecting to ticket purchase...')
    }
  }

  // ============================================
  // ADMIN AUTHENTICATION
  // ============================================
  
  const verifyPassword = async (password) => {
    const hashed = await hashPassword(password)
    return hashed === ADMIN_PASSWORD_HASH
  }

  const handleAdminLogin = async (e) => {
    e.preventDefault()
    const passwordInput = e.target.password
    const rememberMe = e.target.rememberMe?.checked || false
    const password = passwordInput.value
    
    // Check lockout
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const remaining = Math.ceil((lockoutUntil - Date.now()) / 1000 / 60)
      showToast('error', `Account locked. Please try again in ${remaining} minutes.`)
      return
    }
    
    const isValid = await verifyPassword(password)
    
    if (isValid) {
      setIsAdminMode(true)
      setShowAuthModal(false)
      setFailedLoginAttempts(0)
      setLockoutUntil(null)
      localStorage.removeItem('failedLoginAttempts')
      localStorage.removeItem('lockoutUntil')
      
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true')
        localStorage.setItem('adminToken', 'authenticated')
      }
      
      setLastActivity(Date.now())
      passwordInput.value = ''
      showToast('info', 'Admin mode activated')
      
      addActivityLog('login', null, 'Admin logged in')
    } else {
      const newAttempts = failedLoginAttempts + 1
      setFailedLoginAttempts(newAttempts)
      localStorage.setItem('failedLoginAttempts', newAttempts.toString())
      
      if (newAttempts >= RATE_LIMIT_ATTEMPTS) {
        const lockoutTime = Date.now() + RATE_LIMIT_LOCKOUT
        setLockoutUntil(lockoutTime)
        localStorage.setItem('lockoutUntil', lockoutTime.toString())
        showToast('error', 'Too many failed attempts. Account locked for 5 minutes.')
      } else {
        showToast('error', `Invalid password. ${RATE_LIMIT_ATTEMPTS - newAttempts} attempts remaining.`)
      }
      
      // Shake animation
      passwordInput.classList.add('shake')
      setTimeout(() => passwordInput.classList.remove('shake'), 500)
    }
  }

  const exitAdminMode = () => {
    setIsAdminMode(false)
    setSelectedEventId(null)
    localStorage.removeItem('adminToken')
    localStorage.removeItem('rememberMe')
    setLastActivity(Date.now())
    showToast('info', 'Exited admin mode')
    addActivityLog('logout', null, 'Admin logged out')
  }

  // ============================================
  // UNDO/REDO SYSTEM
  // ============================================
  
  const addToUndoStack = (action) => {
    setUndoStack(prev => {
      const newStack = [...prev, action].slice(-MAX_UNDO_STACK)
      // Clear redo stack when new action is added
      setRedoStack([])
      return newStack
    })
    
    // Save to sessionStorage
    try {
      const undoData = {
        undo: [...undoStack, action].slice(-MAX_UNDO_STACK),
        redo: []
      }
      sessionStorage.setItem('undoStack', JSON.stringify(undoData))
    } catch (error) {
      console.error('Failed to save undo stack:', error)
    }
  }

  const handleUndo = () => {
    if (undoStack.length === 0) return
    
    const lastAction = undoStack[undoStack.length - 1]
    setUndoStack(prev => prev.slice(0, -1))
    setRedoStack(prev => [...prev, lastAction])
    
    // Restore previous state
    if (lastAction.type === 'add') {
      setEvents(prev => prev.filter(e => e.id !== lastAction.data.id))
      showToast('success', `Undone: Added ${lastAction.data.name}`, {
        action: 'Redo',
        onAction: handleRedo
      })
    } else if (lastAction.type === 'bulk-add') {
      const bulkIds = lastAction.data.map(e => e.id)
      setEvents(prev => prev.filter(e => !bulkIds.includes(e.id)))
      showToast('success', `Undone: Added ${lastAction.data.length} events`, {
        action: 'Redo',
        onAction: handleRedo
      })
    } else if (lastAction.type === 'edit') {
      setEvents(prev => prev.map(e => 
        e.id === lastAction.data.old.id ? lastAction.data.old : e
      ))
      showToast('success', `Undone: Edited ${lastAction.data.old.name}`, {
        action: 'Redo',
        onAction: handleRedo
      })
    } else if (lastAction.type === 'delete') {
      setEvents(prev => [...prev, lastAction.data].sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date)
        return dateCompare !== 0 ? dateCompare : a.name.localeCompare(b.name)
      }))
      showToast('success', `Undone: Deleted ${lastAction.data.name}`, {
        action: 'Redo',
        onAction: handleRedo
      })
    }
  }

  const handleRedo = () => {
    if (redoStack.length === 0) return
    
    const lastAction = redoStack[redoStack.length - 1]
    setRedoStack(prev => prev.slice(0, -1))
    setUndoStack(prev => [...prev, lastAction])
    
    // Reapply action
    if (lastAction.type === 'add') {
      setEvents(prev => [...prev, lastAction.data].sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date)
        return dateCompare !== 0 ? dateCompare : a.name.localeCompare(b.name)
      }))
    } else if (lastAction.type === 'bulk-add') {
      setEvents(prev => [...prev, ...lastAction.data].sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date)
        return dateCompare !== 0 ? dateCompare : a.name.localeCompare(b.name)
      }))
    } else if (lastAction.type === 'edit') {
      setEvents(prev => prev.map(e => 
        e.id === lastAction.data.new.id ? lastAction.data.new : e
      ))
    } else if (lastAction.type === 'delete') {
      setEvents(prev => prev.filter(e => e.id !== lastAction.data.id))
    }
    
    showToast('success', 'Action redone')
  }

  // ============================================
  // CRUD OPERATIONS
  // ============================================
  
  const handleAddEvent = (e) => {
    e.preventDefault()
    
    const validation = validateEvent({ ...formData, id: generateId() })
    if (!validation.valid) {
      setFormErrors(validation.errors.reduce((acc, err) => {
        if (err.includes('name')) acc.name = err
        else if (err.includes('venue')) acc.venue = err
        else if (err.includes('address')) acc.address = err
        else if (err.includes('date')) acc.date = err
        return acc
      }, {}))
      return
    }
    
    // Check for duplicates
    const newEvent = { ...formData, id: generateId() }
    if (checkDuplicate(newEvent)) {
      if (!confirm('Duplicate event detected. Add anyway?')) {
        return
      }
    }
    
    const sortedEvents = [...events, newEvent].sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date)
      return dateCompare !== 0 ? dateCompare : a.name.localeCompare(b.name)
    })
    
    setEvents(sortedEvents)
    addToUndoStack({ type: 'add', data: newEvent, timestamp: Date.now() })
    addActivityLog('add', newEvent.id, `Added event: ${newEvent.name}`)
    
    setShowAddModal(false)
    setFormData({
      date: new Date().toISOString().split('T')[0],
      name: '',
      venue: '',
      address: ''
    })
    setFormErrors({})
    showToast('success', 'Event added successfully')
    
    // Scroll to new event
    setTimeout(() => {
      const eventElement = document.getElementById(`event-${newEvent.id}`)
      if (eventElement) {
        eventElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        eventElement.classList.add('highlight-new')
        setTimeout(() => eventElement.classList.remove('highlight-new'), 2000)
      }
    }, 100)
  }

  const handleEditEvent = (eventId) => {
    const event = events.find(e => e.id === eventId)
    if (!event) return
    
    setEditingEvent(event)
    setFormData({
      date: event.date,
      name: event.name,
      venue: event.venue,
      address: event.address
    })
    setFormErrors({})
    setShowEditModal(true)
  }

  const handleUpdateEvent = (e) => {
    e.preventDefault()
    
    const validation = validateEvent({ ...formData, id: editingEvent.id })
    if (!validation.valid) {
      setFormErrors(validation.errors.reduce((acc, err) => {
        if (err.includes('name')) acc.name = err
        else if (err.includes('venue')) acc.venue = err
        else if (err.includes('address')) acc.address = err
        else if (err.includes('date')) acc.date = err
        return acc
      }, {}))
      return
    }
    
    const oldEvent = editingEvent
    const updatedEvent = { ...editingEvent, ...formData }
    
    // Check for duplicates (excluding current event)
    if (checkDuplicate(updatedEvent, editingEvent.id)) {
      if (!confirm('Duplicate event detected. Update anyway?')) {
        return
      }
    }
    
    const sortedEvents = events.map(e => 
      e.id === editingEvent.id ? updatedEvent : e
    ).sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date)
      return dateCompare !== 0 ? dateCompare : a.name.localeCompare(b.name)
    })
    
    setEvents(sortedEvents)
    addToUndoStack({ 
      type: 'edit', 
      data: { old: oldEvent, new: updatedEvent }, 
      timestamp: Date.now() 
    })
    addActivityLog('edit', updatedEvent.id, `Edited event: ${updatedEvent.name}`)
    
    setShowEditModal(false)
    setEditingEvent(null)
    showToast('success', 'Event updated successfully')
  }

  const handleDeleteEvent = (eventId) => {
    const event = events.find(e => e.id === eventId)
    if (!event) return
    
    setEditingEvent(event)
    setShowDeleteModal(true)
  }

  const confirmDeleteEvent = () => {
    if (!editingEvent) return
    
    const deletedEvent = editingEvent
    setEvents(prev => prev.filter(e => e.id !== editingEvent.id))
    addToUndoStack({ type: 'delete', data: deletedEvent, timestamp: Date.now() })
    addActivityLog('delete', deletedEvent.id, `Deleted event: ${deletedEvent.name}`)
    
    setShowDeleteModal(false)
    setEditingEvent(null)
    setSelectedEventId(null)
    showToast('success', 'Event deleted', {
      action: 'Undo',
      onAction: handleUndo
    })
  }

  // ============================================
  // EXPORT/IMPORT
  // ============================================
  
  const handleExportJSON = () => {
    try {
      const data = {
        events,
        exportDate: new Date().toISOString(),
        version: '1.0',
        eventCount: events.length
      }
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `events_${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      showToast('success', 'Events exported successfully')
      addActivityLog('export', null, 'Exported events to JSON')
    } catch (error) {
      console.error('Export failed:', error)
      showToast('error', 'Failed to export events')
    }
  }

  const handleImportJSON = (e) => {
    const file = e.target.files[0]
    if (!file) return
    
    if (!file.name.endsWith('.json')) {
      showToast('error', 'Please select a JSON file')
      return
    }
    
    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result)
        const importedEvents = data.events || (Array.isArray(data) ? data : [])
        
        if (!Array.isArray(importedEvents) || importedEvents.length === 0) {
          showToast('error', 'Invalid JSON format. Expected an array of events.')
          return
        }
        
        // Validate all events
        const validEvents = []
        const invalidEvents = []
        
        importedEvents.forEach(event => {
          const validation = validateEvent(event)
          if (validation.valid) {
            validEvents.push({ ...event, id: event.id || generateId() })
          } else {
            invalidEvents.push({ event, errors: validation.errors })
          }
        })
        
        if (invalidEvents.length > 0) {
          console.warn('Some events failed validation:', invalidEvents)
          showToast('warning', `${invalidEvents.length} events failed validation and were skipped`)
        }
        
        if (validEvents.length === 0) {
          showToast('error', 'No valid events found in file')
          return
        }
        
        // Backup current events
        const backup = [...events]
        addToUndoStack({ type: 'import', data: { old: backup, new: validEvents }, timestamp: Date.now() })
        
        // Replace events
        const sortedEvents = validEvents.sort((a, b) => {
          const dateCompare = a.date.localeCompare(b.date)
          return dateCompare !== 0 ? dateCompare : a.name.localeCompare(b.name)
        })
        
        setEvents(sortedEvents)
        showToast('success', `Imported ${validEvents.length} events`)
        addActivityLog('import', null, `Imported ${validEvents.length} events from JSON`)
        
        // Reset file input
        e.target.value = ''
      } catch (error) {
        console.error('Import failed:', error)
        showToast('error', 'Failed to parse JSON file. Please check the format.')
      }
    }
    
    reader.readAsText(file)
  }

  // ============================================
  // ACTIVITY LOG
  // ============================================
  
  const addActivityLog = (action, eventId, description) => {
    const logEntry = {
      id: `log${Date.now()}`,
      timestamp: new Date().toISOString(),
      action,
      eventId,
      description
    }
    
    setActivityLog(prev => {
      const newLog = [...prev, logEntry].slice(-50)
      saveActivityLog(newLog)
      return newLog
    })
  }

  const dismissToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }

  // ============================================
  // KEYBOARD NAVIGATION
  // ============================================
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Admin mode shortcut
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'A') {
        e.preventDefault()
        if (isAdminMode) {
          exitAdminMode()
        } else {
          setShowAuthModal(true)
        }
      }
      
      // ESC key
      if (e.key === 'Escape') {
        if (showQuickAddModal || showQuickAddPreview) {
          setShowQuickAddModal(false)
          setShowQuickAddPreview(false)
          setQuickAddInput('')
        } else if (showAddModal || showEditModal || showDeleteModal || showAuthModal || showSettings) {
          setShowAddModal(false)
          setShowEditModal(false)
          setShowDeleteModal(false)
          setShowAuthModal(false)
          setShowSettings(false)
          setEditingEvent(null)
        } else {
          setSelectedEventId(null)
          setSearchQuery('')
          handleSearch('')
        }
      }
      
      // Keyboard navigation for rows (arrow keys)
      if (!showAddModal && !showEditModal && !showDeleteModal && !showAuthModal) {
        const eventRows = document.querySelectorAll('[role="row"][data-event-id]')
        if (eventRows.length === 0) return
        
        const tableWrapper = tableWrapperRef.current
        
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          keyboardNavIndexRef.current = Math.min(
            keyboardNavIndexRef.current + 1,
            eventRows.length - 1
          )
          const targetRow = eventRows[keyboardNavIndexRef.current]
          const eventId = targetRow.getAttribute('data-event-id')
          setSelectedEventId(eventId)
          targetRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          keyboardNavIndexRef.current = Math.max(keyboardNavIndexRef.current - 1, 0)
          const targetRow = eventRows[keyboardNavIndexRef.current]
          const eventId = targetRow.getAttribute('data-event-id')
          setSelectedEventId(eventId)
          targetRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        } else if ((e.key === 'Enter' || e.key === ' ') && selectedEventId) {
          e.preventDefault()
          // Popup is already shown via selection
        } else if (e.key === 'PageDown' && tableWrapper) {
          // Page down: scroll table down by one viewport height
          e.preventDefault()
          tableWrapper.scrollTop += (tableWrapper.clientHeight - 50)
        } else if (e.key === 'PageUp' && tableWrapper) {
          // Page up: scroll table up by one viewport height
          e.preventDefault()
          tableWrapper.scrollTop -= (tableWrapper.clientHeight - 50)
        } else if (e.key === 'Home' && tableWrapper) {
          // Home: scroll to top
          e.preventDefault()
          tableWrapper.scrollTop = 0
        } else if (e.key === 'End' && tableWrapper) {
          // End: scroll to bottom
          e.preventDefault()
          tableWrapper.scrollTop = tableWrapper.scrollHeight
        }
      }
      
      // Undo/Redo shortcuts (admin mode only)
      if (isAdminMode && (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        e.preventDefault()
        handleUndo()
      } else if (isAdminMode && (e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'Z') {
        e.preventDefault()
        handleRedo()
      } else if (isAdminMode && (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'y') {
        e.preventDefault()
        handleRedo()
      }
      
      // Search shortcut
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        if (searchInputRef.current) {
          searchInputRef.current.focus()
        }
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isAdminMode, selectedEventId, showAddModal, showEditModal, showDeleteModal, showAuthModal, showSettings])

  // ============================================
  // MOBILE TABLE OPTIMIZATION - Functions Defined FIRST
  // ============================================

  /**
   * Debounce helper function
   */
  const debounce = useCallback((func, wait) => {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }, [])

  /**
   * Calculates optimal table height to show exactly 5-6 rows on mobile
   * CRITICAL: Must ensure content is taller than wrapper to enable scrolling
   */
  const calculateMobileTableHeight = useCallback(() => {
    if (window.innerWidth > 768) return // Desktop, skip
    
    const viewportHeight = window.innerHeight
    const headerHeight = 60 // Page header approximate height
    const searchHeight = 56 // Search box height
    const popupHeight = selectedEventId ? 80 : 0 // Bottom popup space
    const adminToolbarHeight = isAdminMode ? 60 : 0
    const padding = 32 // Top and bottom padding
    
    // Available space for table - reduce by 20px to ensure content overflows
    const availableHeight = Math.max(300, viewportHeight 
      - headerHeight 
      - searchHeight 
      - popupHeight 
      - adminToolbarHeight 
      - padding 
      - 20) // Subtract 20px to ensure content always overflows
    
    // Calculate row height to fit 5-6 rows
    const tableHeaderHeight = 45 // Column header height
    const targetRows = 6 // Target visible rows
    const rowHeight = Math.max(60, Math.floor((availableHeight - tableHeaderHeight) / targetRows))
    
    // Set CSS custom properties
    document.documentElement.style.setProperty('--mobile-row-height', `${rowHeight}px`)
    document.documentElement.style.setProperty('--mobile-table-height', `${availableHeight}px`)
    
    // CRITICAL: Force scrolling by ensuring wrapper has explicit height
    const tableWrapper = tableWrapperRef.current
    if (tableWrapper) {
      tableWrapper.style.height = `${availableHeight}px`
      tableWrapper.style.maxHeight = `${availableHeight}px`
      // Force iOS to recognize this as scrollable
      tableWrapper.style.overflowY = 'scroll'
      tableWrapper.style.webkitOverflowScrolling = 'touch'
    }
  }, [isAdminMode, selectedEventId])

  /**
   * Detects if table has horizontal scroll and adds indicator class
   * Updated to work at all zoom levels
   */
  const detectHorizontalScroll = useCallback(() => {
    const tableWrapper = tableWrapperRef.current
    if (!tableWrapper) return
    
    const table = tableWrapper.querySelector('.events-table')
    if (!table) return
    
    const hasHorizontalScroll = table.scrollWidth > tableWrapper.clientWidth
    const scrollLeft = tableWrapper.scrollLeft
    const maxScrollLeft = table.scrollWidth - tableWrapper.clientWidth
    
    // Add class if horizontal scroll is available
    if (hasHorizontalScroll) {
      tableWrapper.classList.add('has-horizontal-scroll')
    } else {
      tableWrapper.classList.remove('has-horizontal-scroll')
    }
    
    // Add classes for scroll position indicators (left/right)
    if (hasHorizontalScroll) {
      if (scrollLeft > 10) {
        tableWrapper.classList.add('can-scroll-left')
        tableWrapper.classList.remove('scrolled-left')
      } else {
        tableWrapper.classList.remove('can-scroll-left')
        tableWrapper.classList.add('scrolled-left')
      }
      
      if (scrollLeft < maxScrollLeft - 10) {
        tableWrapper.classList.add('can-scroll-right')
        tableWrapper.classList.remove('scrolled-right')
      } else {
        tableWrapper.classList.remove('can-scroll-right')
        tableWrapper.classList.add('scrolled-right')
      }
    } else {
      tableWrapper.classList.remove('can-scroll-left', 'can-scroll-right', 'scrolled-left', 'scrolled-right')
    }
  }, [])

  // ============================================
  // SCROLL SHADOWS - Now uses detectHorizontalScroll defined above
  // ============================================
  
  useEffect(() => {
    const tableWrapper = tableWrapperRef.current
    if (!tableWrapper) return
    
    const updateScrollShadows = () => {
      const { scrollTop, scrollHeight, clientHeight } = tableWrapper
      const isScrolledToTop = scrollTop === 0
      const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 1
      
      // Update vertical scroll shadows
      if (scrollShadowTopRef.current) {
        scrollShadowTopRef.current.style.opacity = isScrolledToTop ? '0' : '1'
      }
      if (scrollShadowBottomRef.current) {
        scrollShadowBottomRef.current.style.opacity = isScrolledToBottom ? '0' : '1'
      }
      
      // Mobile scroll indicator (top shadow via CSS class)
      if (window.innerWidth <= 768) {
        if (scrollTop > 10) {
          tableWrapper.classList.add('scrolled')
        } else {
          tableWrapper.classList.remove('scrolled')
        }
      }
      
      // Update horizontal scroll indicators (works at all zoom levels)
      detectHorizontalScroll()
    }
    
    tableWrapper.addEventListener('scroll', updateScrollShadows, { passive: true })
    updateScrollShadows()
    
    return () => {
      tableWrapper.removeEventListener('scroll', updateScrollShadows)
    }
  }, [filteredEvents, detectHorizontalScroll])

  /**
   * Handles mobile row selection with touch events and haptic feedback
   * Only triggers on tap, not on scroll gestures
   */
  const handleMobileRowTouchStart = useCallback((e) => {
    if (window.innerWidth > 768) return // Desktop, use regular click
    
    const touch = e.touches[0]
    const row = e.currentTarget.closest('[data-event-id]')
    if (!row) return
    
    const eventId = row.getAttribute('data-event-id')
    if (!eventId) return
    
    // Store touch start info to detect tap vs scroll
    touchStartRef.current[eventId] = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
      target: row
    }
  }, [])
  
  const handleMobileRowTouchMove = useCallback((e) => {
    if (window.innerWidth > 768) return
    
    const touch = e.touches[0]
    const row = e.currentTarget.closest('[data-event-id]')
    if (!row) return
    
    const eventId = row.getAttribute('data-event-id')
    if (!eventId || !touchStartRef.current[eventId]) return
    
    const start = touchStartRef.current[eventId]
    const deltaX = Math.abs(touch.clientX - start.x)
    const deltaY = Math.abs(touch.clientY - start.y)
    
    // If moved more than 10px, consider it a scroll and don't trigger tap
    if (deltaX > 10 || deltaY > 10) {
      delete touchStartRef.current[eventId]
    }
  }, [])
  
  const handleMobileRowTouchEnd = useCallback((e) => {
    if (window.innerWidth > 768) return
    
    const touch = e.changedTouches[0]
    const row = e.currentTarget.closest('[data-event-id]')
    if (!row) return
    
    const eventId = row.getAttribute('data-event-id')
    if (!eventId || !touchStartRef.current[eventId]) return
    
    const start = touchStartRef.current[eventId]
    const deltaX = Math.abs(touch.clientX - start.x)
    const deltaY = Math.abs(touch.clientY - start.y)
    const deltaTime = Date.now() - start.time
    
    // Only trigger if it's a tap (small movement, quick duration)
    if (deltaX < 10 && deltaY < 10 && deltaTime < 300) {
      // Prevent text selection on tap
      e.preventDefault()
      
      // Handle selection
      handleRowClick(eventId)
      
      // Haptic feedback (if supported)
      if ('vibrate' in navigator) {
        navigator.vibrate(10)
      }
    }
    
    // Clean up
    delete touchStartRef.current[eventId]
  }, [handleRowClick])

  /**
   * Adds 'scrolled' class when table is scrolled down or horizontally
   */
  const handleMobileVerticalScroll = useCallback(() => {
    const tableWrapper = tableWrapperRef.current
    if (!tableWrapper || window.innerWidth > 768) return
    
    // Check both vertical and horizontal scroll
    if (tableWrapper.scrollTop > 10 || tableWrapper.scrollLeft > 10) {
      tableWrapper.classList.add('scrolled')
      // Remember that user has scrolled
      localStorage.setItem('eventsTableScrolled', 'true')
    } else if (tableWrapper.scrollTop === 0 && tableWrapper.scrollLeft === 0) {
      tableWrapper.classList.remove('scrolled')
    }
  }, [])

  // Effect: Initial mobile table height calculation
  useEffect(() => {
    calculateMobileTableHeight()
    
    // Check if user has scrolled before
    const tableWrapper = tableWrapperRef.current
    if (tableWrapper && window.innerWidth <= 768) {
      const hasScrolled = localStorage.getItem('eventsTableScrolled') === 'true'
      if (hasScrolled) {
        tableWrapper.classList.add('scrolled')
      }
      
      // Track horizontal scroll
      const handleScroll = () => {
        handleMobileVerticalScroll()
      }
      
      tableWrapper.addEventListener('scroll', handleScroll, { passive: true })
      return () => {
        tableWrapper.removeEventListener('scroll', handleScroll)
      }
    }
  }, [calculateMobileTableHeight, handleMobileVerticalScroll])

  // Effect: Detect horizontal scroll on mount and when content changes
  useEffect(() => {
    detectHorizontalScroll()
    
    // Verify and ensure scrolling works
    const verifyScrolling = () => {
      const tableWrapper = tableWrapperRef.current
      if (!tableWrapper) return
      
      const table = tableWrapper.querySelector('.events-table')
      if (!table) return
      
      // Ensure overflow is set correctly
      const computedStyle = window.getComputedStyle(tableWrapper)
      if (computedStyle.overflowY === 'hidden' || computedStyle.overflowY === 'visible') {
        console.warn('⚠️ Table wrapper overflow-y is not scrollable!', {
          overflowY: computedStyle.overflowY,
          overflowX: computedStyle.overflowX
        })
        // Force overflow-y to scroll
        tableWrapper.style.overflowY = 'scroll'
        tableWrapper.style.overflowX = 'auto'
      }
      
      // Check if content is scrollable
      const canScrollVertically = table.scrollHeight > tableWrapper.clientHeight
      const canScrollHorizontally = table.scrollWidth > tableWrapper.clientWidth
      
      if (!canScrollVertically && filteredEvents.length > 5) {
        console.warn('⚠️ Table content may not be tall enough to scroll', {
          wrapperHeight: tableWrapper.clientHeight,
          tableHeight: table.scrollHeight,
          eventsCount: filteredEvents.length
        })
      }
      
      console.log('✅ Scroll verification:', {
        canScrollVertically,
        canScrollHorizontally,
        wrapperHeight: tableWrapper.clientHeight,
        tableHeight: table.scrollHeight,
        eventsCount: filteredEvents.length,
        overflowY: computedStyle.overflowY
      })
    }
    
    // Check immediately
    verifyScrolling()
    
    // Also check after rendering completes
    const timeout = setTimeout(() => {
      detectHorizontalScroll()
      verifyScrolling()
    }, 300)
    return () => clearTimeout(timeout)
  }, [filteredEvents, detectHorizontalScroll])

  // Effect: Add touch event listeners to rows on mobile
  // CRITICAL: Don't interfere with table wrapper scrolling
  useEffect(() => {
    if (window.innerWidth > 768) return // Desktop, skip
    
    const tableWrapper = tableWrapperRef.current
    if (!tableWrapper) return
    
    // Ensure table wrapper can scroll on iOS
    const ensureScrolling = () => {
      tableWrapper.style.overflowY = 'scroll'
      tableWrapper.style.webkitOverflowScrolling = 'touch'
      tableWrapper.style.touchAction = 'pan-y pan-x'
    }
    ensureScrolling()
    
    // Re-ensure after a short delay (iOS sometimes needs this)
    setTimeout(ensureScrolling, 100)
    setTimeout(ensureScrolling, 300)
    
    const rows = tableWrapper.querySelectorAll('[data-event-id]')
    
    rows.forEach(row => {
      // Only preventDefault on touchend for actual taps (not scrolls)
      // CRITICAL: All touch events must be passive to allow scrolling
      row.addEventListener('touchstart', handleMobileRowTouchStart, { passive: true })
      row.addEventListener('touchmove', handleMobileRowTouchMove, { passive: true })
      row.addEventListener('touchend', handleMobileRowTouchEnd, { passive: false })
    })
    
    return () => {
      rows.forEach(row => {
        row.removeEventListener('touchstart', handleMobileRowTouchStart)
        row.removeEventListener('touchmove', handleMobileRowTouchMove)
        row.removeEventListener('touchend', handleMobileRowTouchEnd)
      })
    }
  }, [filteredEvents, handleMobileRowTouchStart, handleMobileRowTouchMove, handleMobileRowTouchEnd])

  // ============================================
  // VIEWPORT & ORIENTATION HANDLING
  // ============================================

  // Handle orientation changes and preserve scroll position
  useEffect(() => {
    let previousOrientation = window.orientation !== undefined ? window.orientation : window.innerHeight > window.innerWidth ? 0 : 90
    let resizeTimer
    let rafId

    const handleOrientationChange = () => {
      const tableWrapper = tableWrapperRef.current
      if (!tableWrapper) return

      // Save scroll position
      const scrollPos = tableWrapper.scrollTop

      // Force layout recalculation
      requestAnimationFrame(() => {
        // Restore scroll position after layout
        requestAnimationFrame(() => {
          if (tableWrapper) {
            tableWrapper.scrollTop = scrollPos
          }
        })
      })
    }

    // Debounced resize handler for performance
    const handleResize = () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }

      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        rafId = requestAnimationFrame(() => {
          // Recalculate mobile table height if needed
          if (window.innerWidth <= 768 && tableWrapperRef.current) {
            calculateMobileTableHeight()
          }
          // Detect horizontal scroll changes (works at all zoom levels)
          detectHorizontalScroll()
          
          // Update zoom level on body for CSS targeting
          const zoomLevel = Math.round((window.devicePixelRatio || 1) * 100)
          if (document.body) {
            document.body.setAttribute('data-zoom-level', zoomLevel)
          }
        })
      }, 150)
    }
    
    // Initial zoom level detection
    const initialZoomLevel = Math.round((window.devicePixelRatio || 1) * 100)
    if (document.body) {
      document.body.setAttribute('data-zoom-level', initialZoomLevel)
    }

    // Listen for orientation changes
    window.addEventListener('orientationchange', handleOrientationChange, { passive: true })
    
    // Listen for resize (handles both orientation, window resize, and zoom)
    window.addEventListener('resize', handleResize, { passive: true })
    
    // Listen for zoom events (Ctrl+wheel or pinch zoom)
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        // User is zooming with Ctrl+wheel
        setTimeout(() => {
          handleResize()
        }, 200)
      }
    }
    window.addEventListener('wheel', handleWheel, { passive: true })

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange)
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('wheel', handleWheel)
      if (resizeTimer) clearTimeout(resizeTimer)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [calculateMobileTableHeight, detectHorizontalScroll])

  // ============================================
  // FILTER EVENTS
  // ============================================
  
  useEffect(() => {
    handleSearch(searchQuery)
  }, [searchQuery, events, handleSearch])

  // Set initial filtered events
  useEffect(() => {
    if (events.length > 0 && filteredEvents.length === 0 && !searchQuery) {
      setFilteredEvents(events)
    }
  }, [events])

  // ============================================
  // RENDER
  // ============================================
  
  const groupedEvents = groupByDate(filteredEvents)
  const selectedEvent = events.find(e => e.id === selectedEventId)
  const sortedDates = Object.keys(groupedEvents).sort((a, b) => {
    // Sort by actual date, not formatted string
    const dateA = events.find(e => formatDate(e.date) === a)?.date || ''
    const dateB = events.find(e => formatDate(e.date) === b)?.date || ''
    return dateA.localeCompare(dateB)
  })

  if (isLoading) {
    return (
      <>
        {/* Fixed Background Layer */}
        <div 
          className="background-layer"
          style={{ 
            backgroundImage: `url(${backgroundImage})`,
            backgroundColor: 'transparent' // Ensure no fallback color shows
          }}
        />
        <div className="events-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading events...</p>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {/* Fixed Background Layer - Never Scales */}
      <div 
        className="background-layer"
        style={{ 
          backgroundImage: `url(${backgroundImage})`,
          backgroundColor: 'transparent' // Ensure no fallback color shows
        }}
      />
      
      {/* Scalable Content Layer */}
      <div 
        className={`events-container ${isAdminMode ? 'admin-mode' : ''}`}
      >
      {/* Screen reader announcements */}
      <div id="selection-announcement" className="sr-only" aria-live="polite" aria-atomic="true"></div>
      <div id="search-announcement" className="sr-only" aria-live="polite" aria-atomic="true">
        {searchQuery && filteredEvents.length === 0 && events.length > 0 && (
          `Search for "${searchQuery}" returned no results`
        )}
      </div>
      
      {/* Header */}
      <header className="events-header">
        <div className="header-left">
          <Link to="/" className="home-link">
            <h1>Events</h1>
          </Link>
          <nav className="main-nav">
            <Link 
              to="/" 
              className={`nav-link ${location.pathname === '/' || location.pathname === '/events' || location.pathname === '/events/' ? 'active' : ''}`}
            >
              Events
            </Link>
            <Link 
              to="/my-events" 
              className={`nav-link ${location.pathname === '/my-events' || location.pathname === '/events/my-events' ? 'active' : ''}`}
            >
              My Events
            </Link>
          </nav>
        </div>
        <div className="header-controls">
          <div className="search-wrapper">
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder="Search events, venues, addresses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search events"
            />
            {searchQuery && (
              <button
                className="search-clear"
                onClick={() => {
                  setSearchQuery('')
                  handleSearch('')
                }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          {searchQuery && filteredEvents.length === 0 && events.length > 0 && (
            <span className="search-no-results">
              No Results found
            </span>
          )}
          
          {!isAdminMode && (
            <button
              className="admin-link"
              onClick={() => setShowAuthModal(true)}
              aria-label="Admin login"
            >
              Admin
            </button>
          )}
          
          {isAdminMode && (
            <div className="admin-badge">
              <span className="admin-icon">🔒</span>
              <span>Admin Mode</span>
              <button
                className="exit-admin-btn"
                onClick={exitAdminMode}
                aria-label="Exit admin mode"
              >
                Exit
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Admin Toolbar */}
      {isAdminMode && (
        <div className="admin-toolbar">
          <button className="admin-btn primary" onClick={() => setShowAddModal(true)}>
            <span className="btn-icon">➕</span>
            Add Event
          </button>
          <button className="admin-btn" onClick={() => setShowQuickAddModal(true)}>
            <span className="btn-icon">📋</span>
            Quick Add
          </button>
          <button className="admin-btn" onClick={handleExportJSON}>
            <span className="btn-icon">📥</span>
            Export JSON
          </button>
          <label className="admin-btn">
            <span className="btn-icon">📤</span>
            Import JSON
            <input
              type="file"
              accept=".json"
              onChange={handleImportJSON}
              style={{ display: 'none' }}
            />
          </label>
          <button 
            className="admin-btn" 
            onClick={handleUndo}
            disabled={undoStack.length === 0}
            title={undoStack.length > 0 ? `Undo: ${undoStack[undoStack.length - 1].type}` : ''}
          >
            <span className="btn-icon">↶</span>
            Undo
          </button>
          <button 
            className="admin-btn" 
            onClick={handleRedo}
            disabled={redoStack.length === 0}
            title={redoStack.length > 0 ? `Redo: ${redoStack[redoStack.length - 1].type}` : ''}
          >
            <span className="btn-icon">↷</span>
            Redo
          </button>
          <button className="admin-btn" onClick={() => setShowSettings(true)}>
            <span className="btn-icon">⚙️</span>
            Settings
          </button>
        </div>
      )}

      {/* Table Wrapper */}
          <div 
            className="table-wrapper" 
            ref={tableWrapperRef}
            role="region"
            aria-label="Events table with scrollable content"
            aria-describedby="table-instructions"
            tabIndex={0}
          >
            <div id="table-instructions" className="sr-only">
              Events table with venue and address information. 
              Scroll vertically to see more events.
            </div>
        <div className="scroll-shadow scroll-shadow-top" ref={scrollShadowTopRef}></div>
        
        {sortedDates.length === 0 && !searchQuery ? (
          <div className="empty-state">
            <div className="empty-icon">📅</div>
            <p>No events scheduled</p>
          </div>
        ) : (
          <table role="table" className="events-table">
            <caption className="sr-only">
              Upcoming events with venue and address information. 
              Scroll vertically to see more events. 
              On mobile devices, scroll horizontally to see full addresses.
            </caption>
            <thead>
              <tr>
                <th scope="col">Event</th>
                <th scope="col">Venue</th>
                <th scope="col">Address</th>
                {isAdminMode && <th scope="col" className="admin-col">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sortedDates.map(dateKey => (
                <React.Fragment key={dateKey}>
                  <tr 
                    className="date-header" 
                    role="rowgroup"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <td colSpan={isAdminMode ? 4 : 3} scope="rowgroup">
                      {dateKey}
                    </td>
                  </tr>
                  {groupedEvents[dateKey].map(event => (
                    <tr
                      key={event.id}
                      id={`event-${event.id}`}
                      role="row"
                      data-event-id={event.id}
                      className={selectedEventId === event.id ? 'selected' : ''}
                      onClick={(e) => handleRowClick(event.id, e)}
                      tabIndex={0}
                      aria-selected={selectedEventId === event.id}
                    >
                      <td className="event-name" role="cell">{event.name || '—'}</td>
                      <td className="event-venue" role="cell">{event.venue || '—'}</td>
                      <td className="event-address" role="cell">{event.address || '—'}</td>
                      {isAdminMode && (
                        <td className="admin-actions" role="cell">
                          <button
                            className="icon-btn duplicate-btn"
                            onClick={(e) => {
                              e.stopPropagation()
                              const eventToDuplicate = events.find(e => e.id === event.id)
                              if (eventToDuplicate) {
                                setFormData({
                                  date: new Date().toISOString().split('T')[0],
                                  name: eventToDuplicate.name,
                                  venue: eventToDuplicate.venue,
                                  address: eventToDuplicate.address
                                })
                                setFormErrors({})
                                setShowAddModal(true)
                              }
                            }}
                            aria-label={`Duplicate ${event.name}`}
                            title="Duplicate event"
                          >
                            📋
                          </button>
                          <button
                            className="icon-btn edit-btn"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEditEvent(event.id)
                            }}
                            aria-label={`Edit ${event.name}`}
                            title="Edit event"
                          >
                            ✏️
                          </button>
                          <button
                            className="icon-btn delete-btn"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteEvent(event.id)
                            }}
                            aria-label={`Delete ${event.name}`}
                            title="Delete event"
                          >
                            🗑️
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
        
        <div className="scroll-shadow scroll-shadow-bottom" ref={scrollShadowBottomRef}></div>
      </div>

      {/* Bottom Action Popup */}
      {selectedEventId && selectedEvent && (
        <div className="action-popup" role="dialog" aria-label="Event actions">
          <button
            className="popup-close"
            onClick={() => setSelectedEventId(null)}
            aria-label="Close actions"
          >
            ×
          </button>
          <div className="popup-buttons">
            <button
              className="popup-btn save-btn"
              onClick={() => handlePopupAction('save', selectedEventId)}
            >
              <span className="btn-icon">🔖</span>
              Save
            </button>
            <button
              className="popup-btn tickets-btn"
              onClick={() => handlePopupAction('tickets', selectedEventId)}
            >
              <span className="btn-icon">🎫</span>
              Get Tickets
            </button>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Admin Login</h2>
            <p className="modal-subtitle">Admin mode is for content management only. This is not suitable for sensitive data.</p>
            <form onSubmit={handleAdminLogin}>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  required
                  autoFocus
                  aria-label="Admin password"
                />
              </div>
              <div className="form-group">
                <label>
                  <input type="checkbox" name="rememberMe" />
                  Remember me for 7 days
                </label>
              </div>
              {lockoutUntil && Date.now() < lockoutUntil && (
                <div className="error-message">
                  Account locked. Please try again later.
                </div>
              )}
              <div className="modal-buttons">
                <button type="submit" className="btn primary">Login</button>
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => setShowAuthModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add New Event</h2>
            <form onSubmit={handleAddEvent}>
              <div className="form-group">
                <label htmlFor="add-date">Date *</label>
                <input
                  type="date"
                  id="add-date"
                  required
                  value={formData.date}
                  onChange={(e) => {
                    setFormData({ ...formData, date: e.target.value })
                    setFormErrors({ ...formErrors, date: '' })
                  }}
                  aria-invalid={!!formErrors.date}
                  aria-describedby={formErrors.date ? 'add-date-error' : undefined}
                />
                {formErrors.date && (
                  <span id="add-date-error" className="error-text" role="alert">
                    {formErrors.date}
                  </span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="add-name">Event Name *</label>
                <input
                  type="text"
                  id="add-name"
                  required
                  maxLength={100}
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value })
                    setFormErrors({ ...formErrors, name: '' })
                  }}
                  aria-invalid={!!formErrors.name}
                  aria-describedby={formErrors.name ? 'add-name-error' : undefined}
                />
                {formErrors.name && (
                  <span id="add-name-error" className="error-text" role="alert">
                    {formErrors.name}
                  </span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="add-venue">Venue *</label>
                <input
                  type="text"
                  id="add-venue"
                  required
                  maxLength={100}
                  value={formData.venue}
                  onChange={(e) => {
                    setFormData({ ...formData, venue: e.target.value })
                    setFormErrors({ ...formErrors, venue: '' })
                  }}
                  aria-invalid={!!formErrors.venue}
                  aria-describedby={formErrors.venue ? 'add-venue-error' : undefined}
                />
                {formErrors.venue && (
                  <span id="add-venue-error" className="error-text" role="alert">
                    {formErrors.venue}
                  </span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="add-address">Address *</label>
                <input
                  type="text"
                  id="add-address"
                  required
                  maxLength={200}
                  value={formData.address}
                  onChange={(e) => {
                    setFormData({ ...formData, address: e.target.value })
                    setFormErrors({ ...formErrors, address: '' })
                  }}
                  aria-invalid={!!formErrors.address}
                  aria-describedby={formErrors.address ? 'add-address-error' : undefined}
                />
                {formErrors.address && (
                  <span id="add-address-error" className="error-text" role="alert">
                    {formErrors.address}
                  </span>
                )}
              </div>
              
              <div className="modal-buttons">
                <button type="submit" className="btn primary">Save Event</button>
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => {
                    setShowAddModal(false)
                    setFormErrors({})
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && editingEvent && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Event</h2>
            <form onSubmit={handleUpdateEvent}>
              <div className="form-group">
                <label htmlFor="edit-date">Date *</label>
                <input
                  type="date"
                  id="edit-date"
                  required
                  value={formData.date}
                  onChange={(e) => {
                    setFormData({ ...formData, date: e.target.value })
                    setFormErrors({ ...formErrors, date: '' })
                  }}
                />
                {formErrors.date && (
                  <span className="error-text" role="alert">{formErrors.date}</span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-name">Event Name *</label>
                <input
                  type="text"
                  id="edit-name"
                  required
                  maxLength={100}
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value })
                    setFormErrors({ ...formErrors, name: '' })
                  }}
                />
                {formErrors.name && (
                  <span className="error-text" role="alert">{formErrors.name}</span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-venue">Venue *</label>
                <input
                  type="text"
                  id="edit-venue"
                  required
                  maxLength={100}
                  value={formData.venue}
                  onChange={(e) => {
                    setFormData({ ...formData, venue: e.target.value })
                    setFormErrors({ ...formErrors, venue: '' })
                  }}
                />
                {formErrors.venue && (
                  <span className="error-text" role="alert">{formErrors.venue}</span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-address">Address *</label>
                <input
                  type="text"
                  id="edit-address"
                  required
                  maxLength={200}
                  value={formData.address}
                  onChange={(e) => {
                    setFormData({ ...formData, address: e.target.value })
                    setFormErrors({ ...formErrors, address: '' })
                  }}
                />
                {formErrors.address && (
                  <span className="error-text" role="alert">{formErrors.address}</span>
                )}
              </div>
              
              <div className="modal-buttons">
                <button type="submit" className="btn primary">Save Changes</button>
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingEvent(null)
                    setFormErrors({})
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && editingEvent && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal modal-danger" onClick={(e) => e.stopPropagation()}>
            <h2>Delete Event?</h2>
            <div className="delete-content">
              <p><strong>{editingEvent.name}</strong></p>
              <p>{editingEvent.venue}</p>
              <p>{formatDate(editingEvent.date)}</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-buttons">
              <button
                type="button"
                className="btn danger"
                onClick={confirmDeleteEvent}
              >
                Delete
              </button>
              <button
                type="button"
                className="btn secondary"
                onClick={() => {
                  setShowDeleteModal(false)
                  setEditingEvent(null)
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Modal */}
      {showQuickAddModal && !showQuickAddPreview && (
        <div className="modal-overlay" onClick={() => setShowQuickAddModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <h2>Quick Add Events</h2>
            <p className="modal-subtitle">
              Paste events from Excel/Google Sheets. Format: Date, Event Name, Venue, Address
            </p>
            
            <div className="form-group">
              <label htmlFor="quick-add-input">
                Paste your events (one per line):
              </label>
              <textarea
                ref={quickAddTextareaRef}
                id="quick-add-input"
                className="quick-add-textarea"
                value={quickAddInput}
                onChange={handleQuickAddInputChange}
                placeholder={`Date, Event Name, Venue, Address\n10/31/2025, Halloween Party, Venue Name, 123 Main St, New York, NY\n11/01/2025, Another Event, Another Venue, 456 Oak Ave, New York, NY`}
                rows={12}
                spellCheck={false}
              />
              <div className="quick-add-help">
                💡 Tip: Copy rows from Excel/Sheets and paste here. Header row will be auto-detected and skipped.
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="quick-add-format">Format:</label>
              <select
                id="quick-add-format"
                value={quickAddFormat}
                onChange={(e) => setQuickAddFormat(e.target.value)}
              >
                <option value="auto">Auto-detect (Recommended)</option>
                <option value="csv">CSV (Comma-separated)</option>
                <option value="tab">Tab-separated (Excel/Sheets)</option>
                <option value="pipe">Pipe-separated (|)</option>
              </select>
            </div>
            
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={quickAddOptions.skipDuplicates}
                  onChange={(e) => setQuickAddOptions({
                    ...quickAddOptions,
                    skipDuplicates: e.target.checked
                  })}
                />
                Skip duplicates automatically
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={quickAddOptions.showPreview}
                  onChange={(e) => setQuickAddOptions({
                    ...quickAddOptions,
                    showPreview: e.target.checked
                  })}
                />
                Show preview before adding (recommended)
              </label>
            </div>
            
            <div className="modal-buttons">
              <button
                type="button"
                className="btn secondary"
                onClick={handleDownloadTemplate}
              >
                📥 Download Template
              </button>
              <button
                type="button"
                className="btn secondary"
                onClick={() => {
                  setQuickAddInput('')
                  setQuickAddParsed({ valid: [], errors: [], duplicates: [] })
                }}
              >
                Clear
              </button>
              <button
                type="button"
                className="btn secondary"
                onClick={() => {
                  setShowQuickAddModal(false)
                  setQuickAddInput('')
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={quickAddOptions.showPreview ? handleQuickAddPreview : handleQuickAddSubmit}
              >
                {quickAddOptions.showPreview ? 'Preview →' : 'Add All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Preview Modal */}
      {showQuickAddPreview && (
        <div className="modal-overlay" onClick={() => setShowQuickAddPreview(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <h2>Preview Events</h2>
            
            <div className="preview-summary">
              <div className="preview-stat valid">
                ✓ Valid: {quickAddParsed.valid.length} events
              </div>
              {quickAddParsed.duplicates.length > 0 && (
                <div className="preview-stat duplicate">
                  ⚠ Duplicates: {quickAddParsed.duplicates.length} events
                </div>
              )}
              {quickAddParsed.errors.length > 0 && (
                <div className="preview-stat error">
                  ✗ Errors: {quickAddParsed.errors.length} events
                </div>
              )}
            </div>
            
            {quickAddParsed.valid.length > 0 && (
              <div className="preview-section">
                <h3>Valid Events (Ready to Add)</h3>
                <div className="preview-list">
                  {quickAddParsed.valid.map((event, idx) => (
                    <div key={idx} className="preview-item valid">
                      <span className="preview-icon">✓</span>
                      <div className="preview-content">
                        <strong>{formatDate(event.date)}</strong> - {event.name}
                        <br />
                        <small>{event.venue} • {event.address}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {quickAddParsed.duplicates.length > 0 && (
              <div className="preview-section">
                <h3>Duplicate Events</h3>
                <div className="preview-list">
                  {quickAddParsed.duplicates.map((event, idx) => (
                    <div key={idx} className="preview-item duplicate">
                      <span className="preview-icon">⚠</span>
                      <div className="preview-content">
                        <strong>{formatDate(event.date)}</strong> - {event.name}
                        <br />
                        <small>Already exists in calendar</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {quickAddParsed.errors.length > 0 && (
              <div className="preview-section">
                <h3>Errors</h3>
                <div className="preview-list">
                  {quickAddParsed.errors.map((error, idx) => (
                    <div key={idx} className="preview-item error">
                      <span className="preview-icon">✗</span>
                      <div className="preview-content">
                        <strong>Line {error.line}:</strong> {error.text}
                        <br />
                        <small className="error-details">
                          {error.errors.join('; ')}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="modal-buttons">
              <button
                type="button"
                className="btn secondary"
                onClick={() => {
                  setShowQuickAddPreview(false)
                }}
              >
                ← Back
              </button>
              <button
                type="button"
                className="btn secondary"
                onClick={() => {
                  setShowQuickAddPreview(false)
                  setShowQuickAddModal(false)
                  setQuickAddInput('')
                  setQuickAddParsed({ valid: [], errors: [], duplicates: [] })
                }}
              >
                Cancel
              </button>
              {quickAddParsed.errors.length > 0 && (
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => {
                    const eventsToAdd = quickAddParsed.valid.filter(e => 
                      !quickAddParsed.duplicates.includes(e) || quickAddOptions.skipDuplicates
                    )
                    if (eventsToAdd.length > 0) {
                      setQuickAddParsed({
                        ...quickAddParsed,
                        valid: eventsToAdd
                      })
                      handleQuickAddSubmit()
                    }
                  }}
                >
                  Add {quickAddParsed.valid.length} Valid Only
                </button>
              )}
              <button
                type="button"
                className="btn primary"
                onClick={handleQuickAddSubmit}
              >
                Add All {quickAddParsed.valid.length} Events
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal modal-settings" onClick={(e) => e.stopPropagation()}>
            <h2>Admin Settings</h2>
            <div className="settings-content">
              <div className="settings-section">
                <h3>Activity Log</h3>
                <div className="activity-log">
                  {activityLog.slice(-10).reverse().map(log => (
                    <div key={log.id} className="log-entry">
                      <span className="log-time">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                      <span className="log-description">{log.description}</span>
                    </div>
                  ))}
                  {activityLog.length === 0 && (
                    <p className="empty-log">No activity yet</p>
                  )}
                </div>
              </div>
              
              <div className="settings-section">
                <button
                  className="btn danger"
                  onClick={() => {
                    if (confirm('Are you sure you want to clear all events? This cannot be undone.')) {
                      setEvents([])
                      showToast('success', 'All events cleared')
                      addActivityLog('clear', null, 'Cleared all events')
                    }
                  }}
                >
                  Clear All Events
                </button>
                <button
                  className="btn secondary"
                  onClick={() => {
                    if (confirm('Reset to sample events? This will replace all current events.')) {
                      setEvents(SAMPLE_EVENTS)
                      showToast('success', 'Reset to sample events')
                      addActivityLog('reset', null, 'Reset to sample events')
                    }
                  }}
                >
                  Reset to Sample Events
                </button>
              </div>
            </div>
            <div className="modal-buttons">
              <button
                type="button"
                className="btn secondary"
                onClick={() => setShowSettings(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`toast toast-${toast.type}`}
            role={toast.type === 'error' ? 'alert' : 'status'}
            aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
          >
            <span className="toast-message">{toast.message}</span>
            {toast.action && (
              <button
                className="toast-action"
                onClick={() => {
                  dismissToast(toast.id)
                  if (toast.onAction) toast.onAction()
                }}
              >
                {toast.action}
              </button>
            )}
            <button
              className="toast-close"
              onClick={() => dismissToast(toast.id)}
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      </div>
    </>
  )
}

export default Events
